import { AlertCircle, Loader2, RefreshCw } from "lucide-react"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import {
  getScoringFailureLabel,
  ScoringFailureReason,
  ScoringStatus,
} from "../../enums/ScoringStatus"

interface Props {
  score: number
  scoringStatus?: ScoringStatus
  scoringFailureReason?: ScoringFailureReason
  onRescore?: () => void
  isRescoring?: boolean
  showRescoreButton?: boolean
  className?: string
}

// Renders "Match Score: N%" for scored applications and an "Unscored (reason)" pill
// otherwise. Optionally shows a Rescore button next to the badge.
export function ScoringStatusBadge({
  score,
  scoringStatus,
  scoringFailureReason,
  onRescore,
  isRescoring,
  showRescoreButton,
  className,
}: Props) {
  const status = scoringStatus ?? "Scored"

  if (isRescoring) {
    return (
      <Badge
        variant="outline"
        className={`text-sm px-4 py-1.5 rounded-full font-medium border-blue-400/60 text-blue-800 bg-blue-50 ${className ?? ""}`}
      >
        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        Rescoring…
      </Badge>
    )
  }

  if (status === "Scored") {
    return (
      <Badge
        className={`bg-primary/10 text-primary hover:bg-primary/20 text-sm px-4 py-1.5 rounded-full font-medium shadow-sm border-0 ${className ?? ""}`}
      >
        Match Score: {score}%
      </Badge>
    )
  }

  const label =
    status === "Pending"
      ? "Scoring in progress"
      : `Unscored ${getScoringFailureLabel(scoringFailureReason ?? "Unknown") || "Unknown error"}`

  return (
    <div className={`inline-flex items-center gap-2 ${className ?? ""}`}>
      <Badge
        variant="outline"
        className="text-sm px-4 py-1.5 rounded-full font-medium border-yellow-400/60 text-yellow-800 bg-yellow-50"
      >
        <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
        {label}
      </Badge>
      {showRescoreButton && status !== "Pending" && onRescore && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRescore}
          disabled={isRescoring}
          className="h-8"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Rescore
        </Button>
      )}
    </div>
  )
}
